/**
 * PageHeader — canonical page title block.
 *
 * Encapsulates the breadcrumbs + title/subtitle/actions row that ~21 pages
 * currently hand-roll with drifting spacing (mb-3/4/5/6/8/10, items-start vs
 * items-end, body-sm/subtle vs body-md/muted). One component, one rhythm.
 *
 * The root route (`src/routes/__root.tsx`) already owns outer padding and
 * max-width — this component only handles the in-page header band.
 */
import type { ReactNode } from 'react'
import { Breadcrumbs, type Crumb } from '@/components/shared/Breadcrumbs'
import { Text } from '@/components/ui/Text'
import { cn } from '@/lib/cn'

/**
 * Re-exported from Breadcrumbs, not redeclared.
 *
 * PageHeader hands `crumbs` straight to `<Breadcrumbs>`, so a local narrower copy meant the shell
 * could not express something the component it wraps supports — a crumb with `search`, which
 * UploadedDocumentDetailPage needs for its "Uploaded" tab link. A wrapper's type must be the
 * wrapped component's type.
 */
export type { Crumb }

const ALIGN: Record<'start' | 'center' | 'end', string> = {
  start: 'items-start',
  center: 'items-center',
  end: 'items-end',
}

export interface PageHeaderProps {
  /** Breadcrumb trail. Omit on top-level pages that don't need it. */
  crumbs?: Crumb[]
  /** Page title — string or node (e.g. title + inline badge). */
  title: ReactNode
  /** Optional secondary line: description, live count, status. */
  subtitle?: ReactNode
  /** Right-aligned actions (buttons, links). Hidden if not provided. */
  actions?: ReactNode
  /** Vertical alignment of the title/actions row. Default `start`. */
  align?: 'start' | 'center' | 'end'
  className?: string
}

export function PageHeader({
  crumbs,
  title,
  subtitle,
  actions,
  align = 'start',
  className,
}: PageHeaderProps) {
  return (
    <div className={className}>
      {crumbs && crumbs.length > 0 && <Breadcrumbs crumbs={crumbs} />}
      <div
        className={cn(
          'flex justify-between mt-1 mb-6 mobile-stack mobile-gap-sm',
          ALIGN[align],
        )}
      >
        <div className="min-w-0">
          {typeof title === 'string' ? (
            <Text variant="display-sm" as="h1" className="tracking-tight">
              {title}
            </Text>
          ) : (
            title
          )}
          {subtitle != null &&
            (typeof subtitle === 'string' ? (
              <Text variant="body-sm" color="subtle" className="mt-0.5">
                {subtitle}
              </Text>
            ) : (
              <div className="mt-0.5">{subtitle}</div>
            ))}
        </div>
        {actions && <div className="flex items-center gap-2 shrink-0 mr-2">{actions}</div>}
      </div>
    </div>
  )
}
